import { TAbsoluteLocale } from '@src/config/i18n.config'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'
import { parseLocale } from '@src/utils/i18n.utils'
import { safeSync } from '@src/utils/safe'

import { Attentive } from './integrations/Attentive'
import { Claritas } from './integrations/Claritas'
import { Constructorio } from './integrations/Constructorio'
import { CriteoAnalytics } from './integrations/CriteoAnalytics'
import { DatalayerPixel } from './integrations/DatalayerPixel'
import { EdgeTag } from './integrations/edge-tag'
import { FbPixel } from './integrations/FbPixel'
import { GoogleAdsTag } from './integrations/GoogleAdsTag'
import { GoogleAnalytics } from './integrations/GoogleAnalytics'
import { GoogleAnalytics4 } from './integrations/GoogleAnalytics4'
import { GoogleAnalytics4 as GoogleAnalyticsWebContainer } from './integrations/GoogleWebGtm'
import { Impact } from './integrations/Impact'
import { InternalAnalytics } from './integrations/InternalAnalytics'
import { LinkedInPixel } from './integrations/LinkedIn'
// import { LiveConnect } from './integrations/LiveConnect'
import { PebblePost } from './integrations/PebblePost'
import { PinterestTag } from './integrations/PinterestTag'
import { Poplar } from './integrations/Poplar'
import { RetentionTracking } from './integrations/Retention'
import { ServerSGTMPixel } from './integrations/ServerSGTMPixel'
import { SnapchatPixel } from './integrations/SnapchatPixel'
// import { StylightPixel } from './integrations/StylightPixel'
import { Tatari } from './integrations/Tatari'
import { TikTokPixel } from './integrations/TikTokPixel'
import TrustArcCl from './integrations/trustarc'
import { TelemetryIntegration } from './telemetry-events.types'

type Country = 'US' | 'CA'

const COUNTRY_EXCLUDED_INTEGRATIONS: Record<Country, string[]> = {
  US: [],
  CA: [
    'pebblePost',
    'poplar',
    'CriteoAnalytics',
    'Claritas',
    'linkedInPixel',
    'lc',
    'retentionTracking',
    'scp',
    'styl',
    'Tatari',
  ],
}

const TELEMETRY_INTEGRATION_REGISTRY_MAP_GETTER = () => ({
  attentive: Attentive,
  constructorio: Constructorio,
  claritas: Claritas,
  criteoAnalytics: CriteoAnalytics,
  fbPixel: FbPixel,
  googleAnalytics: GoogleAnalytics,
  googleAnalytics4: GoogleAnalytics4,
  googleAdsTag: GoogleAdsTag,
  googleWebContainer: GoogleAnalyticsWebContainer,
  internalAnalytics: InternalAnalytics,
  impact: Impact,
  linkedInPixel: LinkedInPixel,
  // lc: LiveConnect,
  pebblePost: PebblePost, // only us specific
  poplar: Poplar, // only us specific
  pinterestTag: PinterestTag,
  retentionTracking: RetentionTracking,
  scp: SnapchatPixel,
  // styl: StylightPixel,
  tatari: Tatari,
  tikTokPixel: TikTokPixel,
  datalayerPixel: DatalayerPixel,
  serverSGTMPixel: ServerSGTMPixel,
  edgeTag: EdgeTag,
})

let telemetryIntegrationRegistry: [string, TelemetryIntegration][] = []

function getTelemetryIntegrationRegistry() {
  if (telemetryIntegrationRegistry.length > 0) {
    return telemetryIntegrationRegistry
  }
  telemetryIntegrationRegistry = Object.entries(TELEMETRY_INTEGRATION_REGISTRY_MAP_GETTER())
  return telemetryIntegrationRegistry
}

export function getAllDomains(locale: TAbsoluteLocale, isI18nPreviewEnabled: boolean): string[] {
  const domains: string[] = []
  const { country } = parseLocale(locale)

  const excluded = COUNTRY_EXCLUDED_INTEGRATIONS[country] || []

  getTelemetryIntegrationRegistry().forEach(([_integrationKey, integration]: [string, TelemetryIntegration]) => {
    if ((!isI18nPreviewEnabled || !excluded.includes(_integrationKey)) && integration.config.domain) {
      domains.push(integration.config.domain)
    }
  })
  return domains
}

function checkTrusteCCMConsentBeforeInitialization(
  task: (integrationKey: string, integration: TelemetryIntegration) => void | Promise<void>,
  integration: TelemetryIntegration,
  integrationKey: string,
) {
  TrustArcCl.checkTrusteCCMConsent(integration.config.domain ?? '')
    .then(hasConsent => {
      if (!hasConsent) return
      if (!integration.config.enabled) return
      executeFireAndForgotTask(() => {
        return task(integrationKey, integration)
      }, integration.config.eventIdealTimeout)
    })
    .catch((err: unknown) => {
      console.error('Error checking Truste CCM Consent', err)
    })
}

export function forEachTelemetryIntegration(
  task: (integrationKey: string, integration: TelemetryIntegration) => void | Promise<void>,
) {
  getTelemetryIntegrationRegistry().forEach(
    safeSync(([integrationKey, integration]: [string, TelemetryIntegration]) => {
      // Intitialize the integration if it consent check is skipped
      if (integration.config.skipConsentCheck) {
        if (!integration.config.enabled) return
        task(integrationKey, integration)?.catch((err: unknown) => {
          console.error('Error firing telemetry event', err)
        })
        return
      }

      // Check Truste CCM Consent before initializing the integration
      checkTrusteCCMConsentBeforeInitialization(task, integration, integrationKey)
    }),
  )
}
